import { formatShortMonth, type Locale } from '@croissants/i18n';

/**
 * SEVERAL MORNINGS IN THE LEAST SPACE (founder, 2026-09-21: "suggest a way to
 * list them while taking the min space").
 *
 * The line under the address has one row of a phone to say which mornings a
 * stay covers, so consecutive days collapse into a run and the month is named
 * once per month rather than once per day:
 *
 *   22, 23, 24, 26 Sept        ->  22-24, 26 Sept
 *   29, 30 Sept, 1, 2 Oct      ->  29-30 Sept, 1-2 Oct
 *   one morning                ->  22 Sept
 *
 * A run is drawn with an en dash and a WORD JOINER on each side of it, so a
 * narrow line wraps between runs and never inside one - "22-" at the end of a
 * line reads as a date with something missing.
 *
 * Pure, and over service-day LABELS (`2026-09-22`): a label is a calendar date
 * with no instant in it, so nothing here can drift across a zone boundary.
 */
export function compactDays(days: readonly string[], locale: Locale): string {
  const sorted = [...new Set(days)].sort();
  if (sorted.length === 0) return '';

  /* Runs of consecutive calendar days, never crossing a month: the month is
     printed after the last run of its own days, so a run that straddled two
     would have nowhere honest to put either name. */
  const runs: { first: string; last: string }[] = [];
  for (const day of sorted) {
    const open = runs.at(-1);
    if (open !== undefined && nextDay(open.last) === day && monthOf(open.last) === monthOf(day)) {
      open.last = day;
    } else {
      runs.push({ first: day, last: day });
    }
  }

  const parts: string[] = [];
  let pending: string[] = [];
  let month = monthOf(sorted[0] as string);
  const flush = (): void => {
    if (pending.length > 0) parts.push(`${pending.join(', ')} ${monthName(month, locale)}`);
    pending = [];
  };
  for (const run of runs) {
    if (monthOf(run.first) !== month) {
      flush();
      month = monthOf(run.first);
    }
    const from = String(dayOf(run.first));
    pending.push(run.first === run.last ? from : `${from}⁠–⁠${dayOf(run.last)}`);
  }
  flush();
  return parts.join(', ');
}

/** `2026-09` of `2026-09-22`. */
function monthOf(day: string): string {
  return day.slice(0, 7);
}

function dayOf(day: string): number {
  return Number(day.slice(8, 10));
}

/** The label after this one. Midday UTC keeps the date stable across DST. */
function nextDay(day: string): string {
  const next = new Date(`${day}T12:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/** `Sept` / `sept.` - the short month exactly as the picker's day card and the
    delivery time print it: `formatShortMonth`, which decides the French point. */
function monthName(month: string, locale: Locale): string {
  return formatShortMonth(`${month}-15`, locale);
}
